import { Link } from "react-router-dom";
import { MessageCircle, MapPin } from "lucide-react";
import { eur, fileUrl, fmtLocal, waLink, mapLink } from "../../lib/api";
import { useSite } from "../../lib/store";

export function ArtImage({ image, ratio = "aspect-[4/5]", className = "", eager = false, testid }) {
  return (
    <div className={`passe ${ratio} relative overflow-hidden ${className}`} data-testid={testid}>
      {image ? (
        <img src={fileUrl(image.thumb || image.url)} alt={image.alt || ""} loading={eager ? "eager" : "lazy"} decoding="async"
          width={image.width} height={image.height} className="absolute inset-0 w-full h-full object-contain p-4 sm:p-6" style={{ objectPosition: image.focal || "50% 50%" }} />
      ) : <div className="absolute inset-0 grid place-items-center text-xs text-[#6E6B65]">Kein Bild</div>}
    </div>
  );
}

export function SectionHead({ eyebrow, title, to, cta }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-8">
      <div>
        {eyebrow && <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0047FF] mb-2">{eyebrow}</p>}
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">{title}</h2>
      </div>
      {to && <Link to={to} className="text-sm font-semibold link-u shrink-0">{cta || "Alle ansehen"} →</Link>}
    </div>
  );
}

export const STATUS_DE = { available: "Verfügbar", reserved: "Reserviert", sold: "Verkauft", not_for_sale: "Nicht verkäuflich", on_request: "Auf Anfrage" };

export function ArtCard({ a, i = 0 }) {
  return (
    <Link to={`/kunst/${a.slug}`} className="group block fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`artwork-card-${a.slug}`}>
      <ArtImage image={a.image} />
      <div className="pt-4 space-y-1">
        <div className="flex justify-between gap-3">
          <h3 className="text-lg font-bold leading-tight group-hover:text-[#0047FF] transition-colors">{a.title}</h3>
          <span className={`text-[11px] uppercase tracking-wider font-semibold shrink-0 mt-1 ${a.original_status === "sold" ? "text-[#FF5A5F]" : "text-[#6E6B65]"}`}>{a.print_only ? "Kunstprint" : STATUS_DE[a.original_status] || ""}</span>
        </div>
        <p className="text-sm text-[#6E6B65]">{a.artist?.name}</p>
        <p className="text-sm font-semibold" data-testid="card-price">
          {a.print_only ? (a.print_from != null ? `Print ${eur(a.print_from)} · Bestellanfrage` : "Print auf Anfrage") : a.original_status === "sold" ? "Original verkauft" : a.price_on_request ? "Original: Preis auf Anfrage" : `Original ${eur(a.original_price)}`}
          {!a.print_only && a.print_from != null && <span className="text-[#0047FF]"> · Print ab {eur(a.print_from)}</span>}
        </p>
        {a.venue && <p className="text-xs flex items-center gap-1 text-[#2A2927]"><MapPin size={12} /> Hängt im {a.venue.name}</p>}
      </div>
    </Link>
  );
}

export function FindCard({ f }) {
  return (
    <Link to={`/fundstuecke/${f.slug}`} className="group block" data-testid={`find-card-${f.slug}`}>
      <ArtImage image={(f.images || [])[0]} ratio="aspect-square" />
      <div className="pt-3">
        <p className="text-xs uppercase tracking-wider text-[#6E6B65]">{f.category}</p>
        <h3 className="font-bold text-lg group-hover:text-[#0047FF]">{f.title}</h3>
        <p className="text-sm font-semibold">{(f.stock_available ?? 1) < 1 || f.sold ? "Verkauft" : f.price ? eur(f.price) : "Preis auf Anfrage"}</p>
      </div>
    </Link>
  );
}

export function EventCard({ e }) {
  const cancelled = e.state === "cancelled";
  return (
    <Link to={`/vernissagen/${e.slug}`} className="grid grid-cols-[88px_1fr] gap-4 p-4 bg-white border border-[#E6DFD5] hover:border-[#121212] transition-colors" data-testid={`event-card-${e.slug}`}>
      <div className={`text-center py-3 ${cancelled ? "bg-[#FFEAEA]" : "bg-[#FFC72C]"}`}>
        <p className="font-display font-black text-3xl leading-none">{Number(e.start.slice(8, 10))}</p>
        <p className="text-xs font-semibold uppercase">{new Date(e.start.slice(0, 10)).toLocaleDateString("de-AT", { month: "short" })}</p>
      </div>
      <div>
        {cancelled && <span className="text-xs font-bold text-[#c2181d] uppercase tracking-wider">Abgesagt</span>}
        <h3 className={`font-bold text-lg leading-tight ${cancelled ? "line-through" : ""}`}>{e.title}</h3>
        <p className="text-sm text-[#6E6B65]">{fmtLocal(e.start)} · {e.venue?.name || e.location_text}</p>
        {e.artists?.length > 0 && <p className="text-xs mt-1">{e.artists.map((a) => a.name).join(", ")}</p>}
      </div>
    </Link>
  );
}

export function WhatsAppButton({ area, text, testid = "whatsapp-btn", fallback }) {
  const site = useSite();
  const href = waLink(site, area, text);
  if (!href) return fallback || null;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 h-12 px-5 border-2 border-[#121212] font-semibold hover:bg-[#25D366] hover:border-[#25D366] transition-colors" data-testid={testid}>
      <MessageCircle size={18} /> WhatsApp
    </a>
  );
}

export function MapButton({ venue }) {
  const site = useSite();
  const href = mapLink(site, venue);
  if (!href) return null;
  return <a href={href} target="_blank" rel="noreferrer" className="text-sm font-semibold link-u" data-testid="map-link">Auf Karte öffnen ↗</a>;
}

export function Empty({ children, testid = "empty-state" }) {
  return <div className="border border-dashed border-[#E6DFD5] p-10 text-center text-[#6E6B65]" data-testid={testid}>{children}</div>;
}

export function Loading() {
  return <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-24 text-[#6E6B65]" data-testid="loading">Wird geladen …</div>;
}

export function Page({ children, className = "" }) {
  return <div className={`max-w-[1400px] mx-auto px-4 sm:px-8 py-10 sm:py-16 ${className}`}>{children}</div>;
}

export function PageTitle({ eyebrow, title, children }) {
  return (
    <div className="mb-10 sm:mb-14 max-w-3xl">
      {eyebrow && <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0047FF] mb-3">{eyebrow}</p>}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">{title}</h1>
      {children && <div className="mt-4 text-base text-[#2A2927] leading-relaxed">{children}</div>}
    </div>
  );
}

export function Prose({ text }) {
  return <div className="space-y-4 leading-relaxed text-[#2A2927]">{(text || "").split(/\n\n+/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}</div>;
}

